import { useEffect, useState } from "react";

import * as api from "../lib/api";
import { SessionExpired } from "../lib/api";
import type { AppConfig } from "../lib/config";
import type { Identity } from "../lib/auth";
import type { Student } from "../lib/types";

interface Props {
  config: AppConfig;
  identity: Identity;
  /** Already debounced and trimmed by the caller; never empty. */
  query: string;
  refreshKey: number;
  selectedId: string | null;
  onSelect: (studentId: string) => void;
  onSessionExpired: () => void;
}

export default function SearchPanel({
  config,
  identity,
  query,
  refreshKey,
  selectedId,
  onSelect,
  onSessionExpired,
}: Props) {
  const [students, setStudents] = useState<Student[]>([]);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .searchStudents(config, identity, query)
      .then((result) => {
        if (cancelled) return;
        setStudents(result.students);
        setTotal(result.total);
        setTruncated(result.truncated);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof SessionExpired) onSessionExpired();
        else setError(err instanceof Error ? err.message : "Could not search students.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [config, identity, query, refreshKey, onSessionExpired]);

  if (loading) return <p className="p-4 text-sm text-slate-500">Searching…</p>;
  if (error)
    return (
      <p className="m-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
        {error}
      </p>
    );

  if (students.length === 0)
    return (
      <p className="p-4 text-sm text-slate-500">
        No student matches “{query}”. Try part of a family name, a given name, or a
        student id.
      </p>
    );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Say what was found and whether it was capped — a silently shortened list
          would read as "that is everyone". Pinned above the scrolling rows, so
          reaching the last row cannot read as "that was all of them" either. */}
      <p className="shrink-0 border-b border-slate-100 px-3 py-2 text-xs text-slate-400">
        {total} {total === 1 ? "match" : "matches"} across all programs
        {truncated ? ` · showing the first ${students.length}, narrow the search` : ""}
      </p>
      <ul className="min-h-0 flex-1 divide-y divide-slate-100 overflow-y-auto">
        {students.map((s) => (
          <li key={s.student_id}>
            <button
              onClick={() => onSelect(s.student_id)}
              className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50 ${
                selectedId === s.student_id ? "bg-slate-100" : ""
              }`}
            >
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-slate-800">
                  {s.preferred_name} {s.family_name}
                </span>
                <span className="block text-xs text-slate-400">
                  {s.student_id} · {s.program_code} · WAM {s.wam ?? "—"}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
